import { createReduxModule } from 'hooks-for-redux'
import { AlertEvent } from 'lib/types'
import { getAlertEvents, pusherSubscribeAndBind, sendAlertEvent } from './api'
import { getAlertDisplayMessage, alertEventsAreDuplicates } from 'lib/utils/alertEvents'
import * as NavState from './NavState'
import { PUSHER_PRIVATE_PREFIX } from 'lib/constants'

const ALERT_EVENT_PUSHER_EVENT = 'alertEvent'
const ALERT_SESSION_SUBSCRIPTION_PREFIX = 'alertSession_'

export interface AlertEventsState {
  alertEvents: AlertEvent[]
  loading: boolean
  error?: any
}

const initialState: AlertEventsState = {
  alertEvents: [],
  loading: false,
}

const filterEvents = (events: AlertEvent[]) => {
  // Old function on this line -> consider refactoring current active func -> return events.filter((event, i) => getAlertDisplayMessage(event) && !alertEventsAreDuplicates(event, events[i - 1]))
  return events.filter((event, i) =>
    event.attributes?.map !== null &&
    !(
      event.attributes.location?.floor &&
      event.attributes.location?.integration &&
      event.attributes.location?.property &&
      event.attributes.location?.room
    )
      ? !alertEventsAreDuplicates(event, events[i - 1])
      : getAlertDisplayMessage(event) && !alertEventsAreDuplicates(event, events[i - 1]),
  )
}

export const [use, { setAlertEvents, appendAlertEvent, setLoading, clearLoading, setError }, store] = createReduxModule(
  'alertEvents',
  initialState,
  {
    setAlertEvents: (state: AlertEventsState, alertEvents: AlertEvent[]) => ({
      ...state,
      alertEvents: filterEvents(alertEvents),
    }),
    appendAlertEvent: (state: AlertEventsState, alertEvent: AlertEvent) => ({
      ...state,
      alertEvents: filterEvents([...state.alertEvents, alertEvent]),
    }),
    setLoading: (state: AlertEventsState) => ({ ...state, loading: true }),
    clearLoading: (state: AlertEventsState) => ({ ...state, loading: false }),
    setError: (state: AlertEventsState, error: any) => ({ ...state, error }),
  },
)

const sortAlertEvents = (a: AlertEvent, b: AlertEvent) => a.attributes.timestamp - b.attributes.timestamp

const loadAlertEvents = (alertSessionId: string) =>
  getAlertEvents(alertSessionId)
    .then(events => events.sort(sortAlertEvents))
    .then(events => {
      // Ignore a late response for an alert that is no longer selected, so a
      // slow request for a previously-viewed alert can't overwrite the store
      // with the wrong alert's events after the user has switched alerts.
      if (NavState.store.getState().selectedAlertSessionId !== alertSessionId) return
      setAlertEvents(events)
    })

let oldSelectedAlertSessionId: string | undefined = undefined
let unsubscribePusher: any = () => null
let safeUnsubscribe: any = () => {
  if (unsubscribePusher) {
    unsubscribePusher()
    unsubscribePusher = null
  }
}

setTimeout(() => {
  NavState.store.subscribe(({ selectedAlertSessionId }) => {
    if (!selectedAlertSessionId) {
      safeUnsubscribe()
      // Reset so re-opening the previously-viewed alert re-subscribes and
      // reloads its events (the `!== oldSelectedAlertSessionId` guard below
      // would otherwise treat the same id as "unchanged" and skip the reload).
      oldSelectedAlertSessionId = undefined
      setAlertEvents([])
    } else {
      const alertEventUpdateHandler = ({ data: alertEvent }: { data: AlertEvent }) => {
        // Defensive: the channel is alert-specific, but never let an event that
        // belongs to a different alert session contaminate the current view.
        const eventAlertSessionId = alertEvent?.attributes?.alertSessionId
        if (eventAlertSessionId && eventAlertSessionId !== selectedAlertSessionId) return

        // make sure we didn't miss any events
        loadAlertEvents(selectedAlertSessionId)

        // append the new event for immediate update
        appendAlertEvent({ ...{ action: null }, ...alertEvent })
      }
      const subscribeSuccessHandler = () => {
        // Runs after initial subscription
        // and on reconnect following an outage
        loadAlertEvents(selectedAlertSessionId).then(clearLoading, setError)
      }

      if (selectedAlertSessionId !== oldSelectedAlertSessionId) {
        safeUnsubscribe()
        oldSelectedAlertSessionId = selectedAlertSessionId
        unsubscribePusher = pusherSubscribeAndBind(
          `${PUSHER_PRIVATE_PREFIX}${ALERT_SESSION_SUBSCRIPTION_PREFIX}${selectedAlertSessionId}`,
          ALERT_EVENT_PUSHER_EVENT,
          alertEventUpdateHandler,
          subscribeSuccessHandler,
        )

        setLoading()
        setAlertEvents([])
      }
    }
  })
}, 1)

export const createAlertEvent = (ae: any) => sendAlertEvent(ae)
